﻿/**
 * 用于创建 Swiper 组件，并通过 data-config 配置参数。
 *
 * 通过 snippets/header-script.liquid 全站加载一次（须在 theme 中先加载 swiper-11）。
 * IIFE + 幂等守卫，重复引入也不会二次声明或重复注册。
 *
 * 若配置了 autoplay，默认会等首张可见主图加载完成（或超时）后再开始自动播放，
 * 避免慢网下一直切到 image-placeholder。可用 data-wait-first-image="false" 关闭。
 *
 * @class SwiperSlider
 * @extends {HTMLElement}
 *
 * @example
 *
 * <swiper-slider
 *   data-config='{
 *     "slidesPerView": 4,
 *     "spaceBetween": 24,
 *     "loop": true,
 *     "autoplay": {
 *       "delay": 3000
 *     }
 *   }'
 * >
 *
 * <div class="swiper">
 *   <div class="swiper-wrapper">
 *     <div class="swiper-slide"></div>
 *   </div>
 *
 *   <div class="swiper-pagination"></div>
 *   <button class="swiper-button-prev"></button>
 *   <button class="swiper-button-next"></button>
 * </div>
 * </swiper-slider>
 */
(function () {
  "use strict";

  if (customElements.get("swiper-slider")) return;

  var AUTOPLAY_ARM_FALLBACK_MS = 8000;

  class SwiperSlider extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;
      this._autoplayArmTimer = null;
      this._autoplayArmImg = null;
      this._autoplayArmOnDone = null;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      if (!window.Swiper) {
        console.error("Swiper not loaded");
        return;
      }

      const container = this.querySelector(".swiper");

      if (!container) {
        console.warn("Swiper container not found");
        return;
      }

      this.destroy();

      const userConfig = this.getConfig();
      const tabItems = this.querySelectorAll(".tab-item");
      const tabIndicator = this.querySelector(".tab-indicator");

      const defaultConfig = {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,

        speed: 500,

        watchOverflow: true,

        observer: true,
        observeParents: true,

        navigation: {
          nextEl: this.querySelector(".swiper-button-next"),
          prevEl: this.querySelector(".swiper-button-prev"),
        },

        pagination: {
          el: this.querySelector(".swiper-pagination"),
          clickable: true,
          renderBullet(index, className) {
            return `
            <span class="${className}">
              <span class="bullet-progress"></span>
            </span>
          `;
          },
        },

        on: {
          autoplayTimeLeft(swiper, time, progress) {
            swiper.pagination.bullets.forEach((bullet, index) => {
              const bar = bullet.querySelector(".bullet-progress");

              if (index === swiper.realIndex) {
                bar.style.width = `${(1 - progress) * 100}%`;
              } else {
                bar.style.width = "0%";
              }
            });
          },

          slideChange: (swiper) => {
            swiper.pagination.bullets.forEach((bullet, index) => {
              const bar = bullet.querySelector(".bullet-progress");

              if (!bar) return;

              if (index !== swiper.realIndex || swiper.autoplay?.running) {
                bar.style.width = "0%";
              } else {
                bar.style.width = "100%";
              }
            });

            if (tabItems.length) {
              tabItems.forEach((item) => {
                item.classList.toggle(
                  "active",
                  Number(item.dataset.index) === swiper.realIndex,
                );
              });

              if (tabIndicator) {
                const activeIndex = [...tabItems].findIndex((item) =>
                  item.classList.contains("active"),
                );
                tabIndicator.style.transform =
                  activeIndex > 0
                    ? `translateX(calc(100% + 4px))`
                    : "translateX(0)";
              }
            }
          },
        },
      };

      const config = this.deepMerge(defaultConfig, userConfig);
      const paginationEl = this.querySelector(".swiper-pagination");
      const hasAutoplay = Boolean(config.autoplay);
      const waitForFirstImage =
        hasAutoplay && this.dataset.waitFirstImage !== "false";

      // 如果 paginationEl 包含 style-pagination 类，则添加 is-autoplay 类
      // style-pagination 即progress 指示器，对应snippets/swiper-pagination
      if (paginationEl?.classList.contains("style-pagination")) {
        paginationEl.classList.toggle("is-autoplay", hasAutoplay);
      }

      this.swiper = new Swiper(container, config);

      if (waitForFirstImage && this.swiper.autoplay) {
        this.swiper.autoplay.stop();
        this.armAutoplayAfterFirstImage();
      }

      if (tabItems.length) {
        tabItems.forEach((item) => {
          item.addEventListener("click", () => {
            const index = Number(item.dataset.index);
            if (!isNaN(index) && this.swiper) {
              this.swiper.slideTo(index);
            }
          });
        });
      }
    }

    /**
     * 等当前激活 slide 的主图 load/error，或超时后，再启动 autoplay。
     */
    armAutoplayAfterFirstImage() {
      var self = this;
      var started = false;

      function start() {
        if (started) return;
        started = true;
        self.clearAutoplayArm();

        if (!self.swiper || !self.swiper.autoplay) return;
        self.swiper.autoplay.start();
      }

      this._autoplayArmTimer = setTimeout(start, AUTOPLAY_ARM_FALLBACK_MS);

      var slide =
        (this.swiper.slides && this.swiper.slides[this.swiper.activeIndex]) ||
        this.querySelector(".swiper-slide-active") ||
        this.querySelector(".swiper-slide");

      var mainImg =
        (slide && slide.querySelector("image-loader img")) ||
        (slide && slide.querySelector("img"));

      if (!mainImg) {
        start();
        return;
      }

      if (mainImg.complete && mainImg.naturalWidth > 0) {
        start();
        return;
      }

      this._autoplayArmImg = mainImg;
      this._autoplayArmOnDone = start;
      mainImg.addEventListener("load", start);
      mainImg.addEventListener("error", start);
    }

    clearAutoplayArm() {
      if (this._autoplayArmTimer) {
        clearTimeout(this._autoplayArmTimer);
        this._autoplayArmTimer = null;
      }

      if (this._autoplayArmImg && this._autoplayArmOnDone) {
        this._autoplayArmImg.removeEventListener("load", this._autoplayArmOnDone);
        this._autoplayArmImg.removeEventListener("error", this._autoplayArmOnDone);
      }

      this._autoplayArmImg = null;
      this._autoplayArmOnDone = null;
    }

    destroy() {
      this.clearAutoplayArm();

      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    refresh() {
      this.swiper?.update();
    }

    getConfig() {
      try {
        return JSON.parse(this.dataset.config || "{}");
      } catch (error) {
        console.error("swiper config parse error", error);

        return {};
      }
    }

    deepMerge(target, source) {
      const result = {
        ...target,
      };

      for (const key in source) {
        const value = source[key];

        if (value && typeof value === "object" && !Array.isArray(value)) {
          result[key] = this.deepMerge(target[key] || {}, value);
        } else {
          result[key] = value;
        }
      }

      return result;
    }
  }

  customElements.define("swiper-slider", SwiperSlider);
})();
